// Cvanto Shopping Profile (web) — draft <-> payload logic and the Ukrainian words
// for the shared ids. The SERVER owns the schema and validation
// (server/shoppingProfile/*); this mirrors it for editing and never keeps a
// second vocabulary: ids come from GET /api/shopping-profile `vocab`.

export const STEP_ORDER = ["about", "sizes", "body", "style", "preferences"];

export const STEP_TITLES = {
  about: "Про вас",
  sizes: "Розміри",
  body: "Параметри",
  style: "Стиль",
  preferences: "Вподобання",
};

export const AUDIENCE_LABELS = { women: "Для неї", men: "Для нього" };

export const STYLE_LABELS = {
  casual: "Casual",
  streetwear: "Streetwear",
  classic: "Класика",
  minimal: "Мінімалізм",
  old_money: "Old money",
  sport: "Спорт",
  business: "Ділова",
  romantic: "Романтична",
  vintage: "Вінтаж",
  boho: "Бохо",
};

export const FIT_LABELS = { slim: "Облягаючий", regular: "Звичайний", relaxed: "Вільний", oversized: "Оверсайз" };
export const RISE_LABELS = { low: "Низька", mid: "Середня", high: "Висока" };

export const COLOR_SWATCH = {
  білий: "#ffffff",
  чорний: "#111111",
  сірий: "#9ca3af",
  синій: "#2563eb",
  блакитний: "#7dd3fc",
  червоний: "#ef4444",
  бордовий: "#7f1d1d",
  рожевий: "#f9a8d4",
  зелений: "#22c55e",
  оливковий: "#6b7a3a",
  жовтий: "#facc15",
  помаранчевий: "#fb923c",
  фіолетовий: "#8b5cf6",
  коричневий: "#7c4a21",
  бежевий: "#e7d3b0",
  золотий: "#d4af37",
  срібний: "#cbd5e1",
  різнокольоровий: "#a78bfa",
};

export const POPULAR_BRANDS = ["Zara", "H&M", "Mango", "Reserved", "Nike", "Adidas", "Levi's", "Puma", "New Balance", "Massimo Dutti"];
export const NUMERIC_CLOTHING = [34, 36, 38, 40, 42, 44, 46, 48, 50, 52, 54, 56];
export const JEANS_WAIST = [24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 36, 38];
export const SHOE_EU = [35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47];

export const DEFAULT_RANGES = {
  heightCm: [100, 230],
  weightKg: [25, 250],
  chestCm: [50, 180],
  waistCm: [40, 180],
  hipsCm: [50, 200],
  footLengthCm: [15, 35],
  budget: [0, 1000000],
};

const str = (n) => (n === null || n === undefined ? "" : String(n).replace(".", ","));

export function fromProfile(profile, accountAudience) {
  const p = profile;
  return {
    audience: p?.audience ?? accountAudience ?? null,
    top: p?.sizes?.top ?? null,
    bottom: p?.sizes?.bottom ?? null,
    numeric: p?.sizes?.numeric ?? null,
    jeansWaist: p?.sizes?.jeansWaist ?? null,
    shoeEu: p?.sizes?.shoe?.eu ?? null,
    footLengthCm: str(p?.sizes?.shoe?.footLengthCm),
    heightCm: str(p?.body?.heightCm),
    weightKg: str(p?.body?.weightKg),
    chestCm: str(p?.body?.chestCm),
    waistCm: str(p?.body?.waistCm),
    hipsCm: str(p?.body?.hipsCm),
    styles: p?.styles ?? [],
    fitTop: p?.fit?.top ?? null,
    fitBottom: p?.fit?.bottom ?? null,
    rise: p?.fit?.rise ?? null,
    likeColors: p?.colors?.prefer ?? [],
    avoidColors: p?.colors?.avoid ?? [],
    likeBrands: p?.brands?.prefer ?? [],
    avoidBrands: p?.brands?.avoid ?? [],
    budgetMax: str(p?.budget?.max),
  };
}

// "16,5" -> 16.5 ; "" -> null ; garbage -> NaN
export function parseNumber(text) {
  const t = String(text ?? "").trim().replace(",", ".");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

export function toPayload(d) {
  const n = (t) => {
    const v = parseNumber(t);
    return v === null || Number.isNaN(v) ? null : v;
  };
  const max = n(d.budgetMax);

  return {
    audience: d.audience,
    body: { heightCm: n(d.heightCm), weightKg: n(d.weightKg), chestCm: n(d.chestCm), waistCm: n(d.waistCm), hipsCm: n(d.hipsCm) },
    sizes: {
      top: d.top,
      bottom: d.bottom,
      numeric: d.numeric,
      jeansWaist: d.jeansWaist,
      shoe: { system: "EU", value: d.shoeEu, footLengthCm: n(d.footLengthCm) },
    },
    fit: { top: d.fitTop, bottom: d.fitBottom, rise: d.rise },
    styles: d.styles,
    colors: { prefer: d.likeColors, avoid: d.avoidColors },
    brands: { prefer: d.likeBrands, avoid: d.avoidBrands },
    budget: max ? { max, currency: "UAH" } : null,
  };
}

const NUMERIC_FIELDS = [
  ["heightCm", "heightCm"],
  ["weightKg", "weightKg"],
  ["chestCm", "chestCm"],
  ["waistCm", "waistCm"],
  ["hipsCm", "hipsCm"],
  ["footLengthCm", "footLengthCm"],
  ["budgetMax", "budget"],
];

export function validateDraft(d, ranges = DEFAULT_RANGES) {
  const out = {};
  for (const [key, rangeKey] of NUMERIC_FIELDS) {
    const v = parseNumber(d[key]);
    if (v === null) continue;
    const [min, max] = ranges[rangeKey] || DEFAULT_RANGES[rangeKey];
    if (Number.isNaN(v)) out[key] = "Введіть число";
    else if (v < min || v > max) out[key] = `Від ${min} до ${max}`;
  }
  return out;
}

export const toggle = (list, item, max = 99) => (list.includes(item) ? list.filter((x) => x !== item) : list.length >= max ? list : [...list, item]);

// True when the draft holds nothing the server would store.
export function isDraftEmpty(d) {
  const p = toPayload({ ...d, audience: null });
  const filled = (v) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);
  return !(
    Object.values(p.body).some(filled) ||
    [p.sizes.top, p.sizes.bottom, p.sizes.numeric, p.sizes.jeansWaist, p.sizes.shoe.value, p.sizes.shoe.footLengthCm].some(filled) ||
    Object.values(p.fit).some(filled) ||
    filled(p.styles) ||
    filled(p.colors.prefer) ||
    filled(p.colors.avoid) ||
    filled(p.brands.prefer) ||
    filled(p.brands.avoid) ||
    filled(p.budget)
  );
}

export function sameDraft(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}
